import React from 'react';
import { LockIcon, ZapIcon, ShieldCheckIcon } from 'lucide-react';
import { NavBar } from './NavBar';
import { HERO, HERO_METRICS, OFFER_ANCHOR, STUFFED_IMAGE } from './salesData';
import { HERO_IMAGE_URL } from '../utils/headMeta';

/**
 * Hero mobile-first y compacto: en móvil el orden es promesa → producto →
 * CTA, para que el primer botón entre casi sin scroll. La fotografía del
 * resultado queda debajo de las métricas, sin empujar al CTA.
 *
 * El CTA NO abre el checkout: hace smooth-scroll al primer bloque de precio.
 */
export function Hero() {
  return <div id="top" className="relative overflow-hidden bg-ivory" data-id="element-209" data-mp="x8jojrugd0ju">
      <NavBar data-id="element-210" data-mp="s9q93tvluzh1" />

      <section aria-labelledby="hero-title" className="relative mx-auto w-full max-w-6xl px-5 pb-7 pt-3 sm:px-8 lg:grid lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:gap-x-12 lg:py-12" data-id="element-211" data-mp="sde3ev8vjpwt">
        {/* Promesa */}
        <div className="animate-hero-copy lg:col-start-1 lg:row-start-1" data-id="element-212" data-mp="382p91mw8pvpw">
          <p className="inline-flex items-center gap-2 rounded-full bg-bone px-3 py-1 font-sans text-[0.52rem] font-bold uppercase tracking-editorial text-charcoal sm:text-[0.62rem]" data-id="element-213" data-mp="384s4img5wh20">
            <span className="h-1.5 w-1.5 rounded-full bg-caramel" aria-hidden data-id="element-214" data-mp="3o5j4fdov3pne" />
            {HERO.eyebrow}
          </p>

          <h1 id="hero-title" className="mt-2.5 font-display text-[1.8rem] leading-[1.05] text-charcoal sm:text-[2.8rem] sm:leading-[1.03] lg:text-[3.2rem]" data-id="element-215" data-mp="3e9fs5f0aqamq">
            {HERO.title} <span className="text-[#F47A2A]" data-id="element-216" data-mp="358hlsq2dxr0z">{HERO.titleAccent}</span>
          </h1>

          <p className="mt-2.5 max-w-xl font-sans text-[0.88rem] leading-snug text-charcoal-mid sm:text-[1rem] sm:leading-relaxed" data-id="element-217" data-mp="35akoa9kq5cml">
            {HERO.subtitle}
          </p>
        </div>

        {/* EL PRODUCTO — mockup principal de los 2 tomos + cookies. */}
        <div className="animate-hero-media mt-3 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0" data-id="element-218" data-mp="2hkgl23bg7ew4">
          <figure className="relative" data-id="element-219" data-mp="2lk8usjckvr32">
            <div className="absolute left-1/2 top-1/2 h-[80%] w-[88%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold-pale/70 blur-2xl" aria-hidden data-id="element-220" data-mp="s21iocxnug0i" />
            <div className="mockup-ground absolute bottom-2 left-1/2 h-7 w-[70%] -translate-x-1/2 blur-md sm:h-11" aria-hidden data-id="element-221" data-mp="ic86patlvyvq" />
            <img src={HERO_IMAGE_URL} alt="Los 2 tomos de 100 New York Cookies rodeados de cookies rellenas de chocolate, dulce de leche, pistacho, red velvet y cookies & cream." width={1000} height={800} fetchPriority="high" decoding="async" className="animate-breathe relative mx-auto w-full max-w-[17.5rem] object-contain drop-shadow-[0_22px_36px_rgba(51,39,34,0.24)] sm:max-w-[24rem] lg:max-w-none" data-id="element-222" data-mp="deyl669c87d6" />
            <figcaption className="mt-0.5 text-center font-sans text-[0.55rem] font-bold uppercase tracking-editorial text-[#F47A2A] sm:text-[0.62rem]" data-id="element-223" data-mp="3lquhrx99on9a">
              Lo que recibís · 2 tomos digitales
            </figcaption>
          </figure>
        </div>

        {/* CTA temprano: ancla al primer bloque de precio, nunca al checkout. */}
        <div className="mt-4 lg:col-start-1 lg:row-start-2 lg:mt-6" data-id="element-224" data-mp="269r41x2fzkb3">
          <p className="font-sans text-[0.82rem] font-bold leading-snug text-charcoal sm:text-[0.94rem]" data-id="element-225" data-mp="2b0plx132wqgg">
            {HERO.note}
          </p>

          <a href={OFFER_ANCHOR} className="group mt-3 flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-full bg-[#F47A2A] px-5 py-3 text-center font-sans text-[0.8rem] font-bold uppercase leading-tight tracking-[0.07em] text-white shadow-[0_16px_30px_-12px_rgba(244,122,42,0.55)] transition-[transform,background-color,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:bg-[#DF6A1D] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F47A2A]/50 sm:text-[0.9rem] lg:w-auto lg:px-10" data-id="element-226" data-mp="2fyr8vx3ml9x7">
            {HERO.cta}
            <span className="transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden data-id="element-227" data-mp="4aky925e4qhk">
              →
            </span>
          </a>

          <ul className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-id="element-228" data-mp="e9trfssfm12v">
            <li className="flex items-center gap-1.5 font-sans text-[0.7rem] font-semibold text-charcoal-mid" data-id="element-229" data-mp="9aqme8cgbuuw">
              <LockIcon className="h-3.5 w-3.5 text-[#F47A2A]" aria-hidden data-id="element-230" data-mp="3hphbme5jt1vi" /> Pago seguro
            </li>
            <li className="flex items-center gap-1.5 font-sans text-[0.7rem] font-semibold text-charcoal-mid" data-id="element-231" data-mp="3crz2amnmckmz">
              <ZapIcon className="h-3.5 w-3.5 text-[#F47A2A]" aria-hidden data-id="element-232" data-mp="3rgvhd3o3exx3" /> Acceso inmediato
            </li>
            <li className="flex items-center gap-1.5 font-sans text-[0.7rem] font-semibold text-charcoal-mid" data-id="element-233" data-mp="3mnsl2nrxlad7">
              <ShieldCheckIcon className="h-3.5 w-3.5 text-[#F47A2A]" aria-hidden data-id="element-234" data-mp="2yxyakcv33xw4" /> Garantía de 7
              días
            </li>
          </ul>
        </div>

        {/* Métricas */}
        <div className="lg:col-start-1 lg:row-start-3" data-id="element-235" data-mp="19h2pmgeovg3v">
          <dl className="mt-4 grid grid-cols-4 gap-x-2 border-t border-warm pt-3 sm:gap-x-4" data-id="element-236" data-mp="1e940pz04f0sb">
            {HERO_METRICS.map(metric => <div key={metric.label} className="text-center lg:text-left" data-id="element-237" data-mp="zm2erjl2nlxd">
                <dt className="sr-only" data-id="element-238" data-mp="14isqjupufvcz">{metric.label}</dt>
                <dd data-id="element-239" data-mp="14h81ulq7n6n1">
                  <span className="block font-display text-[1.2rem] leading-none text-caramel sm:text-[1.8rem]" data-id="element-240" data-mp="1s8n5qixmxw0e">
                    {metric.value}
                  </span>
                  <span className="mt-0.5 block font-sans text-[0.5rem] font-semibold leading-tight text-charcoal-mid sm:text-[0.64rem]" data-id="element-241" data-mp="1x5voakmp4226">
                    {metric.label}
                  </span>
                </dd>
              </div>)}
          </dl>
        </div>

        {/* EL RESULTADO — debajo del CTA, para no empujarlo hacia abajo. */}
        <figure className="mt-5 lg:col-start-2 lg:row-start-3 lg:mt-6" data-id="element-242" data-mp="bvoi1frjkywg">
          <img src={STUFFED_IMAGE} alt="New York Cookie gruesa partida al medio con relleno cremoso y chocolate fundido a la vista." width={1200} height={750} loading="lazy" decoding="async" className="aspect-[16/9] w-full rounded-4xl object-cover shadow-bakery sm:rounded-5xl" data-id="element-243" data-mp="2ei3qk7xqedgn" />
          <figcaption className="mt-1.5 text-center font-sans text-[0.55rem] font-bold uppercase tracking-editorial text-charcoal-mid sm:text-[0.62rem]" data-id="element-244" data-mp="1vr2pned1w7ug">
            El resultado
          </figcaption>
        </figure>
      </section>
    </div>;
}